import type { ReactNode } from 'react';
import { Switch, Route, Redirect } from 'wouter';
import { useAuth } from './context/AuthContext.js';
import { PrefsProvider, usePrefs } from './context/PrefsContext.js';
import { DevicesProvider } from './context/DevicesContext.js';
import { I18nProvider, type Locale } from './i18n/index.js';
import { ToastProvider } from './components/ui/Toast.js';
import LoginPage from './pages/LoginPage.js';
import AuthCallbackPage from './pages/AuthCallbackPage.js';
import PasswordResetPage from './pages/PasswordResetPage.js';
import ActivateBridgePage from './pages/ActivateBridgePage.js';
import DashboardPage from './pages/DashboardPage.js';
import InviteAcceptPage from './pages/InviteAcceptPage.js';
import SettingsPage from './pages/settings/SettingsPage.js';
import HomesSettingsPage from './pages/settings/HomesSettingsPage.js';
import AccountSettingsPage from './pages/settings/AccountSettingsPage.js';
import AuditSettingsPage from './pages/settings/AuditSettingsPage.js';
import BatteriesPage from './pages/health/BatteriesPage.js';
import FirmwarePage from './pages/health/FirmwarePage.js';
import ConnectivityPage from './pages/health/ConnectivityPage.js';
import TimelinePage from './pages/TimelinePage.js';
import { AppShell } from './components/AppShell.js';

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { accessToken, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center h-screen"
        style={{ color: 'var(--ac-text-muted)' }}
      >
        Loading…
      </div>
    );
  }
  if (!accessToken) return <Redirect to="/login" />;
  return <>{children}</>;
}

function AppRoutes() {
  const { prefs } = usePrefs();
  const locale: Locale = prefs.locale === 'de-DE' ? 'de-DE' : 'en-US';
  return (
    <I18nProvider locale={locale}>
      <ToastProvider>
        <Switch>
          <Route path="/login" component={LoginPage} />
          <Route path="/auth/callback" component={AuthCallbackPage} />
          <Route path="/auth/password-reset" component={PasswordResetPage} />
          <Route path="/activate" component={ActivateBridgePage} />
          <Route path="/connect-ha">
            <Redirect to="/activate" />
          </Route>
          <Route path="/invites/:token" component={InviteAcceptPage} />
          <Route path="/settings/homes">
            <ProtectedRoute>
              <AppShell>
                <HomesSettingsPage />
              </AppShell>
            </ProtectedRoute>
          </Route>
          <Route path="/settings/account">
            <ProtectedRoute>
              <AppShell>
                <AccountSettingsPage />
              </AppShell>
            </ProtectedRoute>
          </Route>
          <Route path="/settings/audit">
            <ProtectedRoute>
              <AppShell>
                <AuditSettingsPage />
              </AppShell>
            </ProtectedRoute>
          </Route>
          <Route path="/settings">
            <ProtectedRoute>
              <AppShell>
                <SettingsPage />
              </AppShell>
            </ProtectedRoute>
          </Route>
          <Route path="/health/batteries">
            <ProtectedRoute>
              <AppShell>
                <BatteriesPage />
              </AppShell>
            </ProtectedRoute>
          </Route>
          <Route path="/health/firmware">
            <ProtectedRoute>
              <AppShell>
                <FirmwarePage />
              </AppShell>
            </ProtectedRoute>
          </Route>
          <Route path="/health/connectivity">
            <ProtectedRoute>
              <AppShell>
                <ConnectivityPage />
              </AppShell>
            </ProtectedRoute>
          </Route>
          <Route path="/timeline">
            <ProtectedRoute>
              <AppShell>
                <TimelinePage />
              </AppShell>
            </ProtectedRoute>
          </Route>
          <Route path="/">
            <ProtectedRoute>
              <AppShell>
                <DevicesProvider>
                  <DashboardPage />
                </DevicesProvider>
              </AppShell>
            </ProtectedRoute>
          </Route>
        </Switch>
      </ToastProvider>
    </I18nProvider>
  );
}

export function App() {
  return (
    <PrefsProvider>
      <AppRoutes />
    </PrefsProvider>
  );
}
